export const WAKE_PHRASE = '小可同学';
const WAKE_SESSION_MS = 90000;

function mapSpeechError(error) {
  switch (error) {
    case 'not-allowed':
      return '麦克风权限被拒绝，请点击地址栏锁图标允许麦克风';
    case 'network':
    case 'service-not-allowed':
      return '浏览器语音识别需要联网（Chrome 连接 Google 语音服务），请检查网络';
    case 'audio-capture':
      return '未检测到麦克风，请检查系统输入设备';
    default:
      return error ? `语音识别异常：${error}` : '语音识别异常';
  }
}

/** 本地 TTS 播报唤醒应答（不依赖 COCO） */
export function speakLocalWakeAck(text = '我在呢，请说。') {
  if (typeof window === 'undefined' || !window.speechSynthesis) return Promise.resolve(false);
  return new Promise((resolve) => {
    try {
      window.speechSynthesis.cancel();
      const utter = new SpeechSynthesisUtterance(text);
      utter.lang = 'zh-CN';
      utter.rate = 1.05;
      utter.onend = () => resolve(true);
      utter.onerror = () => resolve(false);
      window.speechSynthesis.speak(utter);
    } catch {
      resolve(false);
    }
  });
}

/** 去掉空格与常见标点，便于匹配 STT / Web Speech 结果 */
export function normalizeWakeText(text) {
  return String(text || '')
    .replace(/\s+/g, '')
    .replace(/[，。！？、,.!?]/g, '');
}

/** 模糊匹配唤醒词（兼容 STT 同音误识别） */
export function matchesWakePhrase(text) {
  const normalized = normalizeWakeText(text);
  if (!normalized) return false;
  if (normalized.includes(WAKE_PHRASE)) return true;
  return /小[可科河禾]?同[学]?/.test(normalized) && normalized.length <= 8;
}

export function isWebSpeechWakeSupported() {
  if (typeof window === 'undefined') return false;
  return !!(window.SpeechRecognition || window.webkitSpeechRecognition);
}

/**
 * 浏览器端 Web Speech API 实时检测唤醒词（interimResults），纯本地、无需 COCO。
 */
export function createLocalWakeDetector({ onWake, onHeard, onError } = {}) {
  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SpeechRecognition) {
    return {
      start: () => false,
      stop: () => {},
      resetWake: () => {},
      isSupported: false,
    };
  }

  let recognition = null;
  let running = false;
  let stopped = false;
  let ackedUntil = 0;
  let restartTimer = null;

  const isInWakeSession = () => Date.now() < ackedUntil;

  const clearRestart = () => {
    if (restartTimer) {
      clearTimeout(restartTimer);
      restartTimer = null;
    }
  };

  const scheduleRestart = () => {
    clearRestart();
    if (stopped || !running) return;
    restartTimer = window.setTimeout(() => {
      try {
        recognition?.start();
      } catch {
        scheduleRestart();
      }
    }, 280);
  };

  return {
    isSupported: true,

    start() {
      stopped = false;
      running = true;
      recognition = new SpeechRecognition();
      recognition.lang = 'zh-CN';
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.maxAlternatives = 3;

      recognition.onresult = (event) => {
        for (let i = event.resultIndex; i < event.results.length; i += 1) {
          const text = event.results[i]?.[0]?.transcript || '';
          if (!text.trim()) continue;
          const interim = !event.results[i].isFinal;
          onHeard?.({ text, interim });

          if (isInWakeSession()) continue;
          if (!matchesWakePhrase(text)) continue;

          ackedUntil = Date.now() + WAKE_SESSION_MS;
          onWake?.({
            text,
            interim,
            source: 'local',
          });
          return;
        }
      };

      recognition.onerror = (event) => {
        if (event.error === 'no-speech' || event.error === 'aborted') return;
        onError?.(new Error(mapSpeechError(event.error)));
      };

      recognition.onend = () => {
        if (!stopped && running) scheduleRestart();
      };

      try {
        recognition.start();
        return true;
      } catch (e) {
        onError?.(e);
        return false;
      }
    },

    resetWake() {
      ackedUntil = 0;
    },

    isInWakeSession,

    stop() {
      stopped = true;
      running = false;
      clearRestart();
      try {
        recognition?.stop();
      } catch {
        /* ignore */
      }
      recognition = null;
    },
  };
}
